import { useState, useEffect } from "react";
import { Clock, CheckCircle, FileText } from "lucide-react";
import { getProcessTemplate, type ProcessTemplate, type ProcessVersion } from "../../../api/processes";
import { Badge } from "../../primitives/Badge";
import { cn } from "../../utils/cn";

type ProcessVersionsTabProps = {
  templateId: string;
};

export function ProcessVersionsTab({ templateId }: ProcessVersionsTabProps) {
  const [template, setTemplate] = useState<ProcessTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  const loadTemplate = async () => {
    setIsLoading(true);
    try {
      const data = await getProcessTemplate(templateId, true);
      setTemplate(data);
    } catch (error: any) {
      console.error("Failed to load process template:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Chargement...</div>;
  }

  if (!template) {
    return <div className="p-8 text-center text-muted-foreground">Processus introuvable</div>;
  }

  // For now, we only have current_version in the API response
  // In the future, we'd fetch all versions from a dedicated endpoint
  const versions: Array<ProcessVersion & { is_current?: boolean }> = [];
  if (template.current_version_id) {
    // In a real implementation, we'd fetch all versions
    // const allVersions = await listProcessVersions(templateId);
    // versions = allVersions.map(v => ({ ...v, is_current: v.id === template.current_version_id }));
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border bg-surface p-4">
        <h3 className="text-md font-medium">Historique des versions</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Consultez l'historique des versions publiées de ce processus
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {versions.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <FileText size={48} className="mx-auto mb-4 opacity-50" />
            <p>Aucune version publiée pour le moment</p>
            <p className="text-xs mt-2">
              Les versions apparaîtront ici une fois que vous aurez publié ce processus
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.id}
                className={cn(
                  "p-4 border border-border rounded-lg bg-surface",
                  version.is_current && "border-primary/50 bg-primary/5"
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-semibold">Version {version.version_number}</span>
                      {version.is_current && (
                        <Badge label="Version actuelle" tone="success" />
                      )}
                      {version.published_at ? (
                        <Badge label="Publiée" tone="success" />
                      ) : (
                        <Badge label="Brouillon" tone="warning" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground space-y-1">
                      {version.published_at && (
                        <div className="flex items-center gap-1">
                          <Clock size={12} />
                          <span>Publiée le {new Date(version.published_at).toLocaleDateString("fr-CA")}</span>
                        </div>
                      )}
                      {version.schema_version && (
                        <div>Schéma: {version.schema_version}</div>
                      )}
                      {version.snapshot_hash && (
                        <div className="font-mono text-xs opacity-70">
                          Hash: {version.snapshot_hash.slice(0, 16)}...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
